import React, { ReactElement, useState, useEffect, useRef } from 'react';
import Box from '../../components/Box/Box';
import CategoryMenuBarItem from './CategoryMenuBarItem';
import Icon from '../../components/Icon/Icon';
import BasicModal from '../../components/Modal/BasicModal';
import getComponentClassNames from '../../utils/className/getComponentClassNames';
import { TCategoryItem } from '../../types/TCategoryItem';
import keyboardKeys from '../../constants/keyboardKeys';

const classNames = getComponentClassNames(
  ['CategoryMenuBar'],
  [
    'expanded',
    'topLevel',
    'topLevelItems',
    'expandToggle',
    'expandToggleLabel',
    'expandToggleIcon',
    'background',
    'foldout',
    'list',
    'listItem',
    'noScroll',
    'modalContent',
  ],
);

const { ENTER, ESCAPE } = keyboardKeys;

type TCategoryMenuBarProps = {
  children?: ReactElement;
  topCategories: TCategoryItem[];
  l1Categories: TCategoryItem[];
  toggleLabel: string;
  className?: string;
};

const CategoryMenuBar = ({ topCategories, l1Categories, toggleLabel, ...rest }: TCategoryMenuBarProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isLargeMobile, setIsLargeMobile] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleFocusOut = (event: FocusEvent) => {
    if (overlayRef.current && !overlayRef.current.contains(event.relatedTarget as Node)) {
      setExpanded(false);
    }
  };

  const escapeListener = (event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ESCAPE) {
      setExpanded(false);
    }
  };

  const enterListener = (event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ENTER) {
      setExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    const largeMobile = getComputedStyle(document.documentElement).getPropertyValue('--is-large-mobile') === '1';
    setIsLargeMobile(largeMobile);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escapeListener, false);
    return () => {
      document.removeEventListener('keydown', escapeListener, false);
    };
  });

  useEffect(() => {
    document.body.classList.toggle(classNames.noScroll, isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    const overlayElement = overlayRef.current;
    if (overlayElement) {
      overlayElement.addEventListener('focusout', handleFocusOut, false);
    }

    return () => {
      if (overlayElement) {
        overlayElement.removeEventListener('focusout', handleFocusOut, false);
      }
    };
  }, [isExpanded]);

  const CategoryList = () => (
    <ul className={classNames.list}>
      {l1Categories.map(({ id, name, url, trackingAttribute, icon, badgeLabel }) => (
        <li key={id} className={classNames.listItem}>
          <CategoryMenuBarItem
            key={name}
            href={url}
            icon={icon || id}
            label={name}
            attr={trackingAttribute}
            badgeLabel={badgeLabel}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <Box {...rest} as="nav" name="CategoryMenuBar" className={isExpanded ? classNames.expanded : ''} role="navigation">
      <div className={classNames.topLevel}>
        <div className={classNames.topLevelItems}>
          <div
            className={classNames.expandToggle}
            onClick={() => setExpanded(!isExpanded)}
            onKeyDown={enterListener}
            aria-haspopup="true"
            aria-expanded={isExpanded}
            tabIndex={0}
            role="button"
          >
            <span className={classNames.expandToggleLabel}>{toggleLabel}</span>
            <Icon name="arrow-down" size="m" className={classNames.expandToggleIcon} />
          </div>
          {topCategories.map(({ id, name, url, trackingAttribute, icon, badgeLabel }) => (
            <CategoryMenuBarItem
              href={url}
              key={name}
              icon={icon || id}
              label={name}
              attr={trackingAttribute}
              badgeLabel={badgeLabel}
            />
          ))}
        </div>
      </div>
      {isExpanded && isLargeMobile && (
        <>
          <div
            className={classNames.background}
            onClick={() => setExpanded(false)}
            onKeyDown={escapeListener}
            tabIndex={-1}
          />
          <div className={classNames.foldout} aria-label="submenu" ref={overlayRef}>
            <CategoryList />
          </div>
        </>
      )}
      {isExpanded && !isLargeMobile && (
        <BasicModal
          title={toggleLabel}
          onClose={() => setExpanded(false)}
          contentClassName={classNames.modalContent}
          isBottomSheet={false}
        >
          <CategoryList />
        </BasicModal>
      )}
    </Box>
  );
};

export default CategoryMenuBar;
